
import React, { useState } from 'react';
import { useDeviceDetails } from "@/hooks/use-device-details";
import { useIsMobile } from "@/hooks/use-mobile";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, Camera, Calendar, LayoutGrid, List } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Tables } from "@/integrations/supabase/types";
import { GridLayout } from "../GridLayout";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { TimelapseQuality } from "@/utils/timelapseGenerator";
import { Checkbox } from "@/components/ui/checkbox";

interface DevicePastImagesProps {
  device: {
    device_id: string;
  };
}

export const DevicePastImages: React.FC<DevicePastImagesProps> = ({ device }) => {
  const { device: deviceDetails, isLoading, refreshDevice } = useDeviceDetails(device.device_id);
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [itemLimit, setItemLimit] = useState("25");
  const [layoutType, setLayoutType] = useState<"grid" | "list">("grid");
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [timelapseQuality, setTimelapseQuality] = useState<TimelapseQuality>("medium");
  const [showOnlyAlerts, setShowOnlyAlerts] = useState(false);

  // Query for device images history - consistent with other tab patterns
  const { data: deviceImages, isLoading: isLoadingImages } = useQuery({
    queryKey: ["device-images", device.device_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("device_details")
        .select("device_name, device_description, past_images")
        .eq("device_id", device.device_id)
        .single();

      if (error) throw error;
      return data as Tables<"device_details">;
    },
  });

  const handleRefresh = () => {
    refreshDevice();
    queryClient.invalidateQueries({ queryKey: ["device-images", device.device_id] });
    toast.success("Refreshing device images...");
  };

  const clearDateRange = () => {
    setStartDate(undefined);
    setEndDate(undefined);
  };

  return (
    <div className="p-2">
      {/* Header */}
      <div className={`flex items-center justify-between mb-4 ${isMobile ? 'flex-col space-y-3' : ''}`}>
        <div className="flex items-center">
          <Camera className="h-5 w-5 text-brand-500 mr-2" />
          <h2 className="text-lg font-semibold">Device Images</h2>
        </div>
        <div className={`flex items-center ${isMobile ? 'w-full justify-between' : 'gap-3'}`}>
          {isMobile && (
            <div className="flex items-center border rounded p-1">
              <Button
                variant={layoutType === "grid" ? "default" : "ghost"}
                size="sm"
                onClick={() => setLayoutType("grid")}
                className={cn(
                  "gap-1",
                  isMobile && layoutType === "grid" && "bg-[hsl(var(--lime-button))] text-black hover:bg-[hsl(var(--lime-button))]"
                )}
              >
                <LayoutGrid className="h-4 w-4" />
                Grid
              </Button>
              <Button
                variant={layoutType === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setLayoutType("list")}
                className="gap-1"
              >
                <List className="h-4 w-4" />
                List
              </Button>
            </div>
          )}
          
          <Button variant="outline" size="sm" onClick={handleRefresh} className="gap-2 whitespace-nowrap">
            <RefreshCw className="h-4 w-4 flex-shrink-0" />
            {!isMobile && 'Refresh Images'}
          </Button>
        </div>
      </div>

      <Card className="border bg-muted/50 shadow-none rounded-lg">
        <CardHeader className="py-3 px-4">
          <div className="flex items-center gap-2">
            <Camera className="h-4 w-4 text-brand-500" />
            <CardTitle className="text-base">
              {deviceDetails?.device_name || "Device"} Images
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-0 px-4">
          {isLoading || isLoadingImages ? (
            <div className="space-y-4 w-full max-w-full">
              <Skeleton className="h-8 w-full" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-40 w-full" />
              </div>
            </div>
          ) : (
            <div className="space-y-4 w-full max-w-full">
              {/* Mobile-specific filtering controls */}
              {isMobile && (
                <div className="flex flex-wrap items-center gap-2 p-2 bg-muted/20 rounded-md">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          "w-[120px] justify-start text-left font-normal",
                          !startDate && "text-muted-foreground"
                        )}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "dd/MM/yy") : <span>Start date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarPicker
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          "w-[120px] justify-start text-left font-normal",
                          !endDate && "text-muted-foreground"
                        )}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "dd/MM/yy") : <span>End date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarPicker
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        disabled={(date) =>
                          startDate ? date < startDate : false
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  {(startDate || endDate) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={clearDateRange}
                      className="h-9 w-9"
                    >
                      <span className="sr-only">Clear date range</span>
                      <span className="text-xs">✕</span>
                    </Button>
                  )}

                  <Select 
                    value={itemLimit} 
                    onValueChange={setItemLimit}
                    disabled={!!(startDate && endDate)}
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue placeholder="Show items" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 items</SelectItem>
                      <SelectItem value="25">25 items</SelectItem>
                      <SelectItem value="50">50 items</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="alertsFilter" 
                      checked={showOnlyAlerts}
                      onCheckedChange={(checked) => setShowOnlyAlerts(checked as boolean)}
                    />
                    <label
                      htmlFor="alertsFilter"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Alerts
                    </label>
                  </div>
                </div>
              )}

              {/* Desktop filtering controls - hidden now */}
              {!isMobile && (
                <div className="hidden flex items-center justify-between gap-2 p-2 bg-muted/20 rounded-md">
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(
                            "w-[120px] justify-start text-left font-normal",
                            !startDate && "text-muted-foreground"
                          )}
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {startDate ? format(startDate, "dd/MM/yy") : <span>Start date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarPicker
                          mode="single"
                          selected={startDate}
                          onSelect={setStartDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(
                            "w-[120px] justify-start text-left font-normal",
                            !endDate && "text-muted-foreground"
                          )}
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {endDate ? format(endDate, "dd/MM/yy") : <span>End date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarPicker
                          mode="single"
                          selected={endDate}
                          onSelect={setEndDate}
                          disabled={(date) =>
                            startDate ? date < startDate : false
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>

                    {(startDate || endDate) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={clearDateRange}
                        className="h-9 w-9"
                      >
                        <span className="sr-only">Clear date range</span>
                        <span className="text-xs">✕</span>
                      </Button>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Select 
                      value={itemLimit} 
                      onValueChange={setItemLimit}
                      disabled={!!(startDate && endDate)}
                    >
                      <SelectTrigger className="w-[110px]">
                        <SelectValue placeholder="Show items" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 items</SelectItem>
                        <SelectItem value="25">25 items</SelectItem>
                        <SelectItem value="50">50 items</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <div className="flex items-center border rounded p-1">
                      <Button
                        variant={layoutType === "grid" ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setLayoutType("grid")}
                        className="gap-1"
                      >
                        <LayoutGrid className="h-4 w-4" />
                        Grid
                      </Button>
                      <Button
                        variant={layoutType === "list" ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setLayoutType("list")}
                        className="gap-1"
                      >
                        <List className="h-4 w-4" />
                        List
                      </Button>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="alertsFilter" 
                        checked={showOnlyAlerts}
                        onCheckedChange={(checked) => setShowOnlyAlerts(checked as boolean)}
                      />
                      <label
                        htmlFor="alertsFilter"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Alerts
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Image Grid Component - ensure it respects container width */}
              <div className="w-full max-w-full">
                <GridLayout 
                  deviceId={device.device_id} 
                  deviceName={deviceDetails?.device_name || deviceImages?.device_name}
                  deviceDescription={deviceDetails?.device_description || deviceImages?.device_description}
                  layoutType={layoutType}
                  itemLimit={itemLimit}
                  startDate={startDate}
                  endDate={endDate}
                  isMobileView={isMobile}
                  timelapseQuality={timelapseQuality}
                  showOnlyAlerts={showOnlyAlerts}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DevicePastImages;
